import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, of, switchMap } from 'rxjs';

import { MOBILE_THRESHOLD, TABLET_THRESHOLD, ViewportMode } from '@blaze-shared/elements-domain';
import { resizeRxUtils } from '@blaze-shared/shared-util';

@Injectable({ providedIn: 'root' })
export class ViewportService {
  private mode$$ = new BehaviorSubject<ViewportMode | undefined>(undefined);
  private width$$ = new BehaviorSubject<number | undefined>(undefined);
  private canvasElement$$ = new BehaviorSubject<HTMLElement | null>(null);
  private navHeaderHeight$$ = new BehaviorSubject<number>(0);
  private navBottombarHeight$$ = new BehaviorSubject<number>(0);

  readonly mode$ = this.mode$$.asObservable();
  readonly width$ = this.width$$.asObservable();
  readonly navHeaderHeight$ = this.navHeaderHeight$$.asObservable();
  readonly navBottombarHeight$ = this.navBottombarHeight$$.asObservable();

  get mode(): ViewportMode | undefined {
    return this.mode$$.value;
  }

  get width(): number | undefined {
    return this.width$$.value;
  }

  constructor() {
    // Track canvas element to set current width
    this.canvasElement$$
      .pipe(
        switchMap((canvasElement) => {
          if (!canvasElement) {
            return of(undefined);
          }

          return resizeRxUtils
            .fromResizeObserver(canvasElement)
            .pipe(map((entries) => entries[entries.length - 1].contentRect.width));
        }),
        distinctUntilChanged(),
      )
      .subscribe((width) => {
        this.width$$.next(width);
      });

    // Track width to set current mode
    this.width$
      .pipe(
        map((width) => {
          if (!width) {
            return undefined;
          } else if (width <= MOBILE_THRESHOLD) {
            return 'mobile';
          } else if (width <= TABLET_THRESHOLD) {
            return 'tablet';
          } else {
            return 'desktop';
          }
        }),
        distinctUntilChanged(),
      )
      .subscribe((mode) => {
        this.mode$$.next(mode);
      });
  }

  /**
   * @internal
   * Registers the canvas element
   */
  registerCanvasElement(canvasElement: HTMLElement): void {
    this.canvasElement$$.next(canvasElement);
  }

  /** @internal */
  disposeCanvasElement(canvasElement: HTMLElement): void {
    // Dispose only if the canvas element matches
    // If it's not the same it means that a second canvas was created before disposing the first one
    if (this.canvasElement$$.value === canvasElement) {
      this.canvasElement$$.next(null);
    }
  }

  updateNavHeaderHeight(height: number): void {
    this.navHeaderHeight$$.next(height);
  }

  updateNavBottombarHeight(height: number): void {
    this.navBottombarHeight$$.next(height);
  }
}
